import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  same_name_diff_iban: { label: 'Stesso nome, IBAN diversi', color: 'bg-red-100 text-red-700 border-red-200' },
  same_iban_diff_name: { label: 'Stesso IBAN, nomi diversi', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  fuzzy_name_match: { label: 'Nome simile, IBAN diverso', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  alias_match: { label: 'Alias storico conflittuale', color: 'bg-purple-100 text-purple-700 border-purple-200' },
};

/**
 * Modale di risoluzione anomalie rubrica.
 * Props:
 * - anomalies: array di anomalia (output di detect-anomalies)
 * - transactions: lista transazioni originali (per ottenere quelle "corrette" dopo risoluzione)
 * - context: stringa di contesto (es. "xml_generation" | "xml_upload")
 * - onClose: callback chiusura senza salvare
 * - onResolved: callback invocato con (correctedTransactions) dopo risoluzione confermata
 *
 * NB: lo storico non viene toccato (no backfill di transazioni passate). Si modificano solo
 * rubrica + transazioni in corso di lavorazione.
 */
export default function AnomalyResolutionModal({ anomalies, transactions, context, onClose, onResolved }) {
  const [choices, setChoices] = useState({}); // {anomaly_key: chosen_option_id}
  const [submitting, setSubmitting] = useState(false);

  const allResolved = useMemo(() => anomalies.every(a => choices[a.key]), [anomalies, choices]);

  const handleSubmit = async () => {
    if (!allResolved) {
      toast.error('Risolvi tutte le anomalie prima di continuare');
      return;
    }
    setSubmitting(true);
    try {
      const resolutions = anomalies.map(a => {
        const chosenId = choices[a.key];
        const opt = (a.options || []).find(o => o.id === chosenId) || {};
        return {
          anomaly_key: a.key,
          anomaly_type: a.type,
          chosen_option_id: chosenId,
          chosen_name: opt.name || null,
          chosen_iban: opt.iban || null,
          items: a.items || [],
        };
      });
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/payment-contacts/resolve-anomalies`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolutions, transactions: transactions || [] }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Errore risoluzione anomalie');
        return;
      }
      const data = await r.json();
      toast.success(`Anomalie risolte: ${data.updated_contacts} contatto/i aggiornati. Lo storico non è stato modificato.`);
      onResolved && onResolved(data.corrected_transactions || []);
    } catch (e) {
      toast.error('Errore di rete: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="anomaly-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-700">
            <AlertTriangle className="h-6 w-6" />
            {anomalies.length} anomalie rilevate {context === 'xml_generation' ? 'prima di generare l\'XML' : context === 'xml_upload' ? 'nell\'XML caricato' : ''}
          </DialogTitle>
          <p className="text-sm text-slate-600">
            Correggi i conflitti tra nomi e IBAN prima di procedere. Le correzioni aggiornano la rubrica e le transazioni in corso. Lo storico delle transazioni passate resta intoccato.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {anomalies.map((a, idx) => {
            const cfg = TYPE_LABELS[a.type] || { label: a.type, color: 'bg-slate-100 text-slate-700' };
            const resolved = !!choices[a.key];
            return (
              <div key={a.key} className={`border rounded-lg p-3 ${resolved ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200 bg-white'}`} data-testid={`anomaly-row-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={cfg.color}>{cfg.label}</Badge>
                    <span className="font-medium text-sm text-slate-800">{a.summary}</span>
                  </div>
                  {resolved && <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />}
                </div>

                {/* Items details */}
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(a.items || []).map((it, i) => (
                    <div key={i} className="bg-white border rounded p-2 text-xs">
                      <div className="flex items-center gap-1 mb-1">
                        <Badge variant="outline" className={it.source === 'rubrica' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}>
                          {it.source === 'rubrica' ? '📚 Rubrica' : '📥 Transazione'}
                        </Badge>
                        {it.category && <Badge variant="outline" className="text-[10px]">{it.category}</Badge>}
                      </div>
                      <div className="font-mono text-slate-700">{it.name || '(senza nome)'}</div>
                      <div className="font-mono text-slate-500">{it.iban}</div>
                      {it.amount != null && <div className="text-slate-500 mt-1">€ {Number(it.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>}
                    </div>
                  ))}
                </div>

                {/* Resolution options */}
                <div className="mt-3 space-y-1">
                  <div className="text-xs font-medium text-slate-600 mb-1">Risoluzione:</div>
                  {(a.options || []).map(opt => (
                    <label key={opt.id} className="flex items-center gap-2 cursor-pointer text-sm py-1 px-2 rounded hover:bg-slate-50">
                      <input
                        type="radio"
                        name={`opt_${a.key}`}
                        checked={choices[a.key] === opt.id}
                        onChange={() => setChoices(prev => ({ ...prev, [a.key]: opt.id }))}
                        data-testid={`anomaly-option-${idx}-${opt.id}`}
                      />
                      <span className="text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="border-t pt-3 mt-3">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="anomaly-cancel">
            Annulla operazione
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!allResolved || submitting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="anomaly-confirm"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applicazione...</> : 'Conferma e procedi'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
